import { useState, useEffect, useCallback, type FormEvent } from "react";
import { useToast, useColorModeValue } from "@chakra-ui/react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter,
  Button, FormControl, FormLabel, Input, Textarea, VStack, Flex, Switch, Text,
  SimpleGrid, Spinner, Icon,
} from "@chakra-ui/react";
import { FileText, FilePlus } from "lucide-react";
import { useApiRequest } from "../../../hooks/useApiRequest";
import { settings } from "../../../shared/settings";
import type { FormTemplate, FormType } from "./types";

const base = () => settings.environment.VITE_PROCESS_HUB_BASE_URL ?? "";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  formType: FormType | null;
  onSuccess: () => void;
}

export function FormTypeModal({ isOpen, onClose, formType, onSuccess }: Props) {
  const toast = useToast();
  const { request } = useApiRequest();

  const titleColor  = useColorModeValue("#434347", "#e8edf2");
  const labelColor  = useColorModeValue("#434347", "#e8edf2");
  const descColor   = useColorModeValue("#696a71", "#8fa3b8");
  const cardBorder  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const cardBg      = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const inputBg     = useColorModeValue("white", "rgba(255,255,255,0.05)");
  const inputBorder = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");
  const iconChipBg  = useColorModeValue("rgba(0, 158, 251, 0.1)", "rgba(0, 158, 251, 0.16)");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [mode, setMode] = useState<"picker" | "form">("form");
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [creatingTemplateKey, setCreatingTemplateKey] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const data = await request({
        url: `${base()}/api/v1/forms/templates`,
        method: "GET",
        skipToast: true,
      });
      setTemplates(data);
    } catch {
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [request]);

  useEffect(() => {
    if (isOpen) {
      if (formType) {
        setMode("form");
        setName(formType.name);
        setDescription(formType.description ?? "");
        setIsActive(formType.is_active);
      } else {
        setMode("picker");
        setName("");
        setDescription("");
        setIsActive(true);
        loadTemplates();
      }
    }
  }, [isOpen, formType, loadTemplates]);

  const handleUseTemplate = async (template: FormTemplate) => {
    setCreatingTemplateKey(template.key);
    try {
      await request({
        url: `${base()}/api/v1/forms/from-template`,
        method: "POST",
        payload: { template_key: template.key },
        skipToast: true,
      });
      toast({ title: "Formulário criado a partir do template", status: "success", duration: 3000, isClosable: true });
      onSuccess();
      onClose();
    } catch {
      toast({ title: "Erro ao criar formulário a partir do template", status: "error", duration: 4000, isClosable: true });
    } finally {
      setCreatingTemplateKey(null);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({ title: "O nome é obrigatório", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    try {
      setSubmitting(true);
      if (formType) {
        await request({
          url: `${base()}/api/v1/forms/${formType.id}`,
          method: "PATCH",
          payload: { name, description: description || null, is_active: isActive },
          skipToast: true,
        });
      } else {
        await request({
          url: `${base()}/api/v1/forms`,
          method: "POST",
          payload: { name, description: description || null },
          skipToast: true,
        });
      }
      toast({
        title: formType ? "Formulário atualizado" : "Formulário criado",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onSuccess();
      onClose();
    } catch {
      toast({ title: "Erro ao salvar formulário", status: "error", duration: 4000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === "picker") {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="2xl" mx={4}>
          <ModalHeader fontSize="lg" fontWeight={600} color={titleColor} borderBottomWidth="1px" borderColor={cardBorder} pb={4}>
            Novo Formulário
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pt={5} pb={6}>
            <Text fontSize="sm" color={descColor} mb={4}>
              Escolha um dos formulários padrão da empresa ou comece em branco.
            </Text>
            {loadingTemplates ? (
              <Flex justify="center" py={8}>
                <Spinner size="md" color="#009efb" />
              </Flex>
            ) : (
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                <Flex
                  bg={cardBg}
                  border="1px dashed"
                  borderColor={cardBorder}
                  borderRadius="12px"
                  p={4}
                  align="center"
                  gap={3}
                  cursor="pointer"
                  _hover={{ borderColor: "#009efb" }}
                  onClick={() => setMode("form")}
                >
                  <Flex align="center" justify="center" w="36px" h="36px" borderRadius="10px" bg={iconChipBg} flexShrink={0}>
                    <Icon as={FilePlus} boxSize={4} color="#009efb" />
                  </Flex>
                  <Text fontSize="sm" fontWeight={600} color={titleColor}>Formulário em branco</Text>
                </Flex>

                {templates.map((template) => (
                  <Flex
                    key={template.key}
                    bg={cardBg}
                    border="1px solid"
                    borderColor={cardBorder}
                    borderRadius="12px"
                    p={4}
                    align="center"
                    gap={3}
                    cursor={creatingTemplateKey ? "default" : "pointer"}
                    opacity={creatingTemplateKey && creatingTemplateKey !== template.key ? 0.6 : 1}
                    _hover={{ borderColor: "#009efb" }}
                    onClick={() => !creatingTemplateKey && handleUseTemplate(template)}
                  >
                    <Flex align="center" justify="center" w="36px" h="36px" borderRadius="10px" bg={iconChipBg} flexShrink={0}>
                      {creatingTemplateKey === template.key ? (
                        <Spinner size="sm" color="#009efb" />
                      ) : (
                        <Icon as={FileText} boxSize={4} color="#009efb" />
                      )}
                    </Flex>
                    <Flex direction="column">
                      <Text fontSize="sm" fontWeight={600} color={titleColor}>{template.name}</Text>
                      <Text fontSize="xs" color={descColor}>{template.field_count} campos</Text>
                    </Flex>
                  </Flex>
                ))}
              </SimpleGrid>
            )}
          </ModalBody>

          <ModalFooter borderTopWidth="1px" borderColor={cardBorder} gap={2} pt={4}>
            <Button variant="ghost" borderRadius="50px" size="sm" onClick={onClose}>Cancelar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit} borderRadius="2xl" mx={4}>
        <ModalHeader
          fontSize="lg"
          fontWeight={600}
          color={titleColor}
          borderBottomWidth="1px"
          borderColor={cardBorder}
          pb={4}
        >
          {formType ? "Editar Formulário" : "Novo Formulário"}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pt={5}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Nome</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Solicitação de Aumento"
                bg={inputBg}
                borderColor={inputBorder}
                borderRadius="8px"
                fontSize="sm"
                _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Descrição</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva o propósito deste formulário..."
                bg={inputBg}
                borderColor={inputBorder}
                borderRadius="8px"
                fontSize="sm"
                resize="none"
                rows={3}
                _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
              />
            </FormControl>

            {formType && (
              <FormControl>
                <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Ativo</FormLabel>
                <Flex align="center" gap={3} h="40px">
                  <Switch
                    colorScheme="blue"
                    isChecked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <Text fontSize="sm" color={labelColor}>{isActive ? "Sim" : "Não"}</Text>
                </Flex>
              </FormControl>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={cardBorder} gap={2} pt={4}>
          {!formType && (
            <Button variant="ghost" borderRadius="50px" size="sm" onClick={() => setMode("picker")} mr="auto">
              Voltar
            </Button>
          )}
          <Button variant="ghost" borderRadius="50px" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            bg="#009efb"
            color="white"
            _hover={{ bg: "#007bd4" }}
            borderRadius="50px"
            size="sm"
            type="submit"
            isLoading={submitting}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
